import React from 'react';
import classNames from 'classnames';
import { formatNumber } from 'humanize-plus';

import Link from 'buyside/components/presentation/Link/link';
import Skeleton, {
  SKELETON_SIZES,
  SKELETON_WIDTHS
} from 'buyside/components/presentation/Skeleton/skeleton';

import { entityUrl } from 'buyside/util/urlHelper';

import { InsiderTradeFragment } from 'interfaces/graphql-typings';

import styles from './tradesTableStyles.scss';

const TradesTable: React.FC<{
  loading: boolean;
  tableName: string;
  trades: InsiderTradeFragment[];
}> = ({ loading, tableName, trades }) => {
  return (
    <div className={styles.tradesTableContainer} id={`${tableName}-table`}>
      <table className={styles.tradesTable}>
        <thead>
          <tr>
            <th data-cell="Name / Title">Name / Title</th>
            <th data-cell="Date">Date</th>
            <th data-cell="Symbol">Symbol</th>
            <th data-cell="Purchase / Sale">Purchase / Sale</th>
            <th data-cell="Shares">Shares</th>
            <th data-cell="Share Price">Share Price</th>
            <th data-cell="Ownership Change">Ownership Change</th>
          </tr>
        </thead>
        <tbody>
          {trades.map(trade => (
            <tr id={`${tableName}-${trade.id}`} key={`${tableName}-${trade.id}`} tabIndex={0}>
              <td data-cell="Name / Title">
                <p>
                  <strong>{trade.name}</strong>
                </p>
                <p>
                  <i>{trade.title}</i>
                </p>
              </td>
              <td data-cell="Date">{trade.date}</td>
              <td data-cell="Symbol">
                {trade.entityId ? (
                  <Link className={styles.goldChip} to={entityUrl(trade.entityId)}>
                    {trade.symbol}
                  </Link>
                ) : (
                  <span className={classNames(styles.goldChip, styles.disabled)}>
                    {trade.symbol}
                  </span>
                )}
              </td>
              <td
                data-cell="Purchase / Sale"
                className={classNames(styles.colored, {
                  [styles.red]: trade.tradeType === 'Sale'
                })}
              >
                <strong>{trade.tradeType}</strong>
              </td>
              <td data-cell="Shares">{formatNumber(trade.numShares)}</td>
              <td data-cell="Share Price">{trade.sharePrice}</td>
              <td data-cell="Ownership Change">{trade.ownershipChangePct}</td>
            </tr>
          ))}
          {loading ? (
            [...Array(10).keys()].map(key => (
              <tr key={`${tableName}-loading-${key}`}>
                <td data-cell="Name / Title">
                  <p>
                    <Skeleton size={SKELETON_SIZES.SMALL} width={SKELETON_WIDTHS.SMALL} />
                  </p>
                  <p>
                    <Skeleton size={SKELETON_SIZES.SMALL} width={SKELETON_WIDTHS.MEDIUM} />
                  </p>
                </td>
                <td data-cell="Date">
                  <Skeleton size={SKELETON_SIZES.SMALL} width={SKELETON_WIDTHS.TINY} />
                </td>
                <td data-cell="Symbol">
                  <Skeleton size={SKELETON_SIZES.SMALL} width={SKELETON_WIDTHS.TINY} />
                </td>
                <td data-cell="Purchase / Sale">
                  <Skeleton size={SKELETON_SIZES.SMALL} width={SKELETON_WIDTHS.TINY} />
                </td>
                <td data-cell="Shares">
                  <Skeleton size={SKELETON_SIZES.SMALL} width={SKELETON_WIDTHS.TINY} />
                </td>
                <td data-cell="Share Price">
                  <Skeleton size={SKELETON_SIZES.SMALL} width={SKELETON_WIDTHS.TINY} />
                </td>
                <td data-cell="Ownership Change">
                  <Skeleton size={SKELETON_SIZES.SMALL} width={SKELETON_WIDTHS.TINY} />
                </td>
              </tr>
            ))
          ) : trades.length ? null : (
            <tr>
              <td colSpan={7} data-cell="Name / Title">
                <p>No Results.</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TradesTable;
